import { jsx as _jsx } from "react/jsx-runtime";
import { existsFilter } from '@iotv/core-services';
import { BranchingQueryDefinition, edgeTypeMatrix, getDescendingGroupRecuriveQuery, getItemTypeToGroupNestedAscendingQueryDef, } from '@iotv/datamodel-core';
import { AdjacentType, getZombieInstance, } from '@iotv/iotv-v3-types';
import React, { useEffect } from 'react';
import { ConsumerDao } from '../../../../../data/ConsumerDAO';
import { ventureImports } from '../../../../../venture';
import { GROUPING_PSEUDO_ROW_TYPE_ID } from './QueryTableContext';
import { LazyQueryTable } from './QueryTableInternal';
const { REACT_APP_DEBUG } = process.env;
const debug = REACT_APP_DEBUG && false;
const dashboardLazyTableConfig = ventureImports.dashboardLazyTableConfig;
export const LazyQueryTableShim = ({ contextCustomer, applicationData, userFunctions, isSmallView }) => {
    const getParams = ({ contextCustomer, dashboardObjectTypeId, }) => {
        const input = {
            items: [contextCustomer],
            groupDefiningObjectTypeId: 'Customer',
            supportedType: dashboardObjectTypeId,
            includeSupportedTypeInstances: true,
            addIdPrefixes: true,
            supportedTypeSubQueries: [
                new BranchingQueryDefinition({
                    id: `${dashboardObjectTypeId}-Incidents`,
                    path: [{ objectTypeId: 'Incident', adjacencyType: AdjacentType.CHILD, edgeTypeId: undefined }],
                }),
            ],
        };
        return input;
    };
    useEffect(() => {
        const getRulesets = async () => {
            if (!contextCustomer)
                return;
            const { err, data } = await ConsumerDao.getGraphQueryResponse({
                contextObjects: [contextCustomer],
                path: [{ objectTypeId: 'Ruleset', adjacencyType: AdjacentType.CHILD, edgeTypeId: edgeTypeMatrix.Customer.Ruleset.customerHasRuleset.edgeTypeId }],
            });
            if (err || !data)
                console.error(`Error fetching rulesets for customer ${contextCustomer.sk}: ${err ? err.message : 'No response'}`);
            if (data) {
                debug && console.log(`Fetched rulesets for customer ${contextCustomer.sk}: `, data);
                const items = [...data.hierarchy.nodes, data.hierarchy.edges];
                userFunctions.addObjectsToState(items);
            }
        };
        getRulesets();
    }, [contextCustomer?.sk]);
    const domainExtractor = (previousQueryLevel, previousSequenceId, item) => {
        /**
         * use case-specific handling for domain rows
         * remove this for generics based on edge type matrix / datamodel
         */
        let groupingPseudoRow = null;
        if (typeof item.domain === 'string') {
            // special handling to create fake domain rows from groups.domain
            const { domain } = item;
            const domainItem = getZombieInstance({
                type: GROUPING_PSEUDO_ROW_TYPE_ID,
                id: domain,
                name: domain,
            });
            debug && console.log(`Creating a domain ${item.domain} which may already exist`);
            groupingPseudoRow = {
                ...domainItem,
                queryLevel: 0,
                sequenceId: `${domainItem.sk}`,
                sequenceName: `${domainItem.name}`,
                contextObject: item,
                visible: true,
                expanded: false,
                queries: [],
                querying: false,
                queryExecuted: true,
                dailyReadings: null,
                hourlyReadings: null,
                bucketStrategyMap: null,
                readingsAttributeKey: null,
                isSmallView,
            };
        }
        return {
            groupingPseudoRow,
        };
    };
    const groupingFunction = domainExtractor;
    const getLazyQueryInput = ({ dashboardObjectTypeId, contextQuery, readingsBucketStrategyMap, readingsAttributeKey, cellRenderers, }) => {
        if (!contextCustomer)
            return;
        const rulesetsQuery = getItemTypeToGroupNestedAscendingQueryDef({
            groupDefiningObjectTypeId: 'Customer',
            sourceItemType: dashboardObjectTypeId,
            finalSubQueries: [
                new BranchingQueryDefinition({
                    id: `${dashboardObjectTypeId}-Ruleset`,
                    path: [{ objectTypeId: 'Ruleset', adjacencyType: AdjacentType.PARENT, edgeTypeId: edgeTypeMatrix.Ruleset?.Group.ruleset_has_group.edgeTypeId }],
                }),
            ],
        });
        if (contextQuery instanceof BranchingQueryDefinition) {
            const lazyQuery = {
                dashboardObjectTypeId,
                query: contextQuery,
                rootObjects: [contextCustomer],
                groupingFunction,
                bucketStrategyMap: readingsBucketStrategyMap,
                readingsAttributeKey,
                cellRenderers,
            };
            return lazyQuery;
        }
        else if (typeof contextQuery === 'string') {
            const input = getParams({ contextCustomer, dashboardObjectTypeId: contextQuery });
            input.supportedTypeSubQueries?.push(rulesetsQuery);
            const apexQuery = getDescendingGroupRecuriveQuery(input);
            const lazyQuery = {
                dashboardObjectTypeId,
                query: apexQuery.topLevelQuery,
                rootObjects: [contextCustomer],
                groupingFunction,
                bucketStrategyMap: readingsBucketStrategyMap,
                readingsAttributeKey,
                cellRenderers,
            };
            return lazyQuery;
        }
        else {
            const input = getParams({ contextCustomer, dashboardObjectTypeId });
            input.supportedTypeSubQueries?.push(rulesetsQuery);
            const apexQuery = getDescendingGroupRecuriveQuery(input);
            const lazyQuery = {
                dashboardObjectTypeId,
                query: apexQuery.topLevelQuery,
                rootObjects: [contextCustomer],
                groupingFunction,
                bucketStrategyMap: readingsBucketStrategyMap,
                readingsAttributeKey,
                cellRenderers,
            };
            return lazyQuery;
        }
    };
    /** the groupingFunction: GroupingFunction is added to each separate LazyQueryInput */
    const { columnRenderers, objectTypeConfigs } = dashboardLazyTableConfig ?? { columnRenderers: {} };
    const tableQueries = objectTypeConfigs?.map((params) => getLazyQueryInput(params)).filter(existsFilter) ?? [];
    return _jsx(LazyQueryTable, { queries: tableQueries, columnRenderers, applicationData, userFunctions, isSmallView }, contextCustomer?.sk);
};
